import { Children } from 'react';
import { s } from '@/theme/tokens';
import { Panel } from '@/ui/composites/Panel';
import { Box, Surface } from '@/ui/primitives';
import { TABLE_PANEL_HEADER_HEIGHT } from '../tableSystem/tableConstants';
import { buildChartPanelSurfaceSx } from './chartPanelSurfaceSx';
import type { ReactElement, ReactNode } from 'react';

type MetricChartPanelProps = {
  header: ReactNode;
  children: ReactNode;
  width?: number;
  height?: number;
  headerHeight?: number;
  panelPaddingTop?: string | number;
  panelPaddingBottom?: string | number;
  headerPaddingX?: string | number;
  headerPaddingTop?: string | number;
  headerPaddingBottom?: string | number;
  contentPaddingX?: string | number;
  contentPaddingTop?: string | number;
  contentPaddingBottom?: string | number;
};

export const MetricChartPanel = ({
  header,
  children,
  width,
  height,
  headerHeight = TABLE_PANEL_HEADER_HEIGHT,
  panelPaddingTop = s('none'),
  panelPaddingBottom = s('xxs'),
  headerPaddingX = s('xl'),
  headerPaddingTop = s('xxs'),
  headerPaddingBottom = s('xxs'),
  contentPaddingX = s('xl'),
  contentPaddingTop = s('none'),
  contentPaddingBottom = s('xxs'),
}: MetricChartPanelProps): ReactElement => {
  const headerNode = (
    <Box
      sx={{
        minHeight: `${headerHeight}px`,
        height: `${headerHeight}px`,
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        px: headerPaddingX,
        pt: headerPaddingTop,
        pb: headerPaddingBottom,
        boxSizing: 'border-box',
        flex: '0 0 auto',
      }}
    >
      {Children.toArray(header)}
    </Box>
  );

  if (typeof height === 'number') {
    return (
      <Surface
        padding="none"
        sx={[
          ...buildChartPanelSurfaceSx({ width, height }),
          {
            display: 'flex',
            flexDirection: 'column',
            px: s('md'),
          },
        ]}
      >
        <Box
          sx={{
            pt: panelPaddingTop,
            pb: panelPaddingBottom,
            display: 'flex',
            flex: '1 1 auto',
            minHeight: 0,
            flexDirection: 'column',
          }}
        >
          {headerNode}
          <Box
            sx={{
              px: contentPaddingX,
              pt: contentPaddingTop,
              pb: contentPaddingBottom,
              boxSizing: 'border-box',
              flex: '1 1 auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {Children.toArray(children)}
          </Box>
        </Box>
      </Surface>
    );
  }

  return (
    <Panel
      padding={null}
      gap="none"
      contentGap="none"
      headerGap="none"
      headerSlot={headerNode}
      sx={[
        ...buildChartPanelSurfaceSx({ width, height, heightMode: 'minHeight' }),
        {
          pt: panelPaddingTop,
          pb: panelPaddingBottom,
        },
      ]}
    >
      <Box
        sx={{
          px: contentPaddingX,
          pt: contentPaddingTop,
          pb: contentPaddingBottom,
          boxSizing: 'border-box',
        }}
      >
        {Children.toArray(children)}
      </Box>
    </Panel>
  );
};
